import { useMemo } from "react";
import { LEAGUE } from "../../config/league";
import type { DraftStateDTO, LeaderboardDTO } from "../../shared/types";
import { PickAnnounce } from "../components/PickAnnounce";
import { PickClock } from "../components/PickClock";
import { ReturnChart, lastReturn } from "../components/ReturnChart";
import { Sparkline } from "../components/Sparkline";
import { DirectionTag, LivePill, ReturnValue } from "../components/ui";
import { api } from "../lib/api";
import { managerColor } from "../lib/colors";
import { formatMoney, formatPct, formatTimeFromISO, useEtClock, usePoll } from "../lib/format";

/** Chrome-free full-screen overlay for the YouTube stream. */
export default function BroadcastPage({
  view = "leaderboard",
}: {
  view?: "leaderboard" | "draft" | "race";
}) {
  if (view === "draft") return <BroadcastDraft />;
  if (view === "race") return <BroadcastRace />;
  return <BroadcastLeaderboard />;
}

function BroadcastRace() {
  const { data } = usePoll(api.history, LEAGUE.leaderboardPollSec * 1000);
  const clock = useEtClock();

  const series = useMemo(
    () =>
      (data?.managers ?? [])
        .map((m) => ({
          name: m.name,
          color: managerColor(m.seed),
          points: m.points,
        }))
        .sort((a, b) => lastReturn(b) - lastReturn(a)), // leader first
    [data],
  );

  return (
    <div className="title-safe flex min-h-screen flex-col">
      <div className="mb-8 flex items-end justify-between">
        <h1 className="text-6xl font-black tracking-tight text-fg">
          Season Race
          <span className="ml-3 align-middle text-2xl font-semibold text-muted">
            {LEAGUE.seasonName}
          </span>
        </h1>
        <span className="num text-2xl text-muted">{clock} ET</span>
      </div>

      <div className="flex-1">
        <ReturnChart series={series} height="calc(100vh - 15rem)" xAxis />
      </div>

      <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3">
        {series.map((s) => {
          const last = s.points[s.points.length - 1]?.r;
          return (
            <span key={s.name} className="flex items-center gap-3 text-3xl font-bold">
              <span
                className="h-5 w-5 rounded-full"
                style={{ background: s.color }}
              />
              <span className="text-fg">{s.name}</span>
              {last !== undefined && (
                <span className="num text-muted">{formatPct(last)}</span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function BroadcastLeaderboard() {
  const { data } = usePoll(api.leaderboard, LEAGUE.leaderboardPollSec * 1000);
  const clock = useEtClock();

  return (
    <div className="title-safe min-h-screen">
      <div className="mb-8 flex items-end justify-between">
        <h1 className="text-6xl font-black tracking-tight text-fg">
          {LEAGUE.brandName}
          <span className="ml-3 align-middle text-2xl font-semibold text-muted">
            {LEAGUE.seasonName}
          </span>
        </h1>
        <div className="flex items-center gap-5">
          {data && <LivePill live={data.marketOpen} />}
          <span className="num text-2xl text-muted">{clock} ET</span>
        </div>
      </div>

      <div className="space-y-4">
        {data?.entries.map((e) => (
          <div
            key={e.id}
            className={`flex items-center gap-8 rounded-2xl border px-8 py-6 ${
              e.rank === 1
                ? "border-gold/50 bg-gold/5"
                : "border-edge bg-ink-800/70"
            }`}
          >
            <span
              className={`num relative w-16 text-center text-5xl font-black ${
                e.rank === 1
                  ? "text-gold"
                  : e.rank === 2
                    ? "text-silver"
                    : e.rank === 3
                      ? "text-bronze"
                      : "text-faint"
              }`}
            >
              {e.rank === 1 && (
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-2xl">
                  ♛
                </span>
              )}
              {e.rank}
            </span>
            <span className="flex-1 text-5xl font-extrabold text-fg">{e.name}</span>
            <div className="w-64">
              <Sparkline data={e.sparkline} height={64} />
            </div>
            <ReturnValue value={e.avgReturn} className="w-72 text-right text-6xl" showArrow />
          </div>
        ))}
      </div>

      {data && (
        <p className="num mt-6 text-right text-lg text-faint">
          updated {formatTimeFromISO(data.updatedAt)} ET
        </p>
      )}
    </div>
  );
}

function BroadcastDraft() {
  const { data } = usePoll(api.draftState, LEAGUE.draftPollSec * 1000);

  return (
    <div className="title-safe min-h-screen">
      {data && <PickAnnounce state={data} />}
      {data && data.status !== "done" && (
        <div className="mb-10 flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold uppercase tracking-[0.3em] text-clock">
              On the clock
            </div>
            <div className="mt-2 animate-pulseclock text-8xl font-black text-fg">
              {data.onTheClock?.name ?? "—"}
            </div>
            {data.nextUp && (
              <div className="mt-4 text-3xl text-muted">
                Next: <span className="font-bold text-fg">{data.nextUp}</span>
              </div>
            )}
          </div>
          {data.status === "live" && (
            <PickClock
              startedAt={data.pickStartedAt}
              seconds={data.pickClockSec}
              size="lg"
            />
          )}
          <div className="num text-right">
            <div className="text-8xl font-black text-fg">
              {Math.min(data.currentPick, data.totalPicks)}
              <span className="text-5xl text-faint">/{data.totalPicks}</span>
            </div>
            <div className="text-3xl text-muted">
              Round {data.currentRound} · Pick {data.pickInRound}
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-1.5">
            <thead>
              <tr>
                <th className="w-12" />
                {data.managers.map((m) => (
                  <th
                    key={m.id}
                    className={`pb-2 text-center text-2xl font-bold ${
                      data.onTheClock?.id === m.id ? "text-clock" : "text-fg"
                    }`}
                  >
                    {m.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: LEAGUE.numRounds }, (_, i) => i + 1).map((round) => (
                <tr key={round}>
                  <td className="num text-center text-xl text-faint">{round}</td>
                  {data.managers.map((m) => {
                    const pick = m.picks[round - 1];
                    const isCurrent =
                      data.status !== "done" &&
                      data.onTheClock?.id === m.id &&
                      data.currentRound === round;
                    return (
                      <td
                        key={m.id}
                        className={`rounded-xl border px-3 py-3 text-center ${
                          pick
                            ? "border-edge bg-ink-900/70"
                            : isCurrent
                              ? "animate-pulseclock border-clock bg-clock/10"
                              : "border-dashed border-edge/50"
                        }`}
                      >
                        {pick ? (
                          <>
                            <div className="num text-2xl font-bold text-fg">
                              {pick.ticker}
                            </div>
                            <div className="mt-1 flex items-center justify-center gap-2">
                              <DirectionTag direction={pick.direction} />
                              <span className="num text-sm text-faint">
                                {formatMoney(pick.costBasis)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <span className="text-base text-faint">
                            {isCurrent ? "•" : ""}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
